import { useCallback } from "react";
import { container } from "tsyringe";
import { XMarkIcon } from "@heroicons/react/20/solid";
import ContributorResponseResource from "common/resources/Project/Config/Contributor/ContributorResponseResource";
import ProjectTeamRoleContributorsRequestResource from "common/resources/Project/Config/Team/Role/ProjectTeamRoleContributorsRequestResource";
import ProjectTeamRoleContributorsResponseResource from "common/resources/Project/Config/Team/Role/ProjectTeamRoleContributorsResponseResource";
import Button, { EButtonColor, EButtonVariant } from "components/elements/Button";
import Modal from "components/elements/Modal";
import Typography from "components/elements/Typography";
import Form, { useForm } from "components/materials/Form";
import Input from "components/materials/Form/InputElement";
import I18n from "components/materials/I18n";
import { useProjectTeamContext } from "components/pages/ProfessionalSpace/contexts/ProjectTeam";
import { useProjectId } from "hooks/useProjectId";
import { translateISO369 } from "utils/I18nText";
import RoleService from "services/projects/RoleService";
import { EContributorModal, useContributorModal } from "../../ContributorModal";
import CreateContributorModal from "../../ContributorModal/CreateContributorModal";
import EditContributorModal from "../../ContributorModal/EditContributorModal";
import RemoveContributorModal from "../../ContributorModal/RemoveContributorModal";
import RoleMember from "./RoleMember";
import classes from "./classes.module.scss";

type IProps = {
	role: ProjectTeamRoleContributorsResponseResource;
	isOpen: boolean;
	onClose: () => void;
	onRoleEdited: (role: ProjectTeamRoleContributorsResponseResource) => void;
	onContributorAdded: (contributor: ContributorResponseResource) => void;
	onContributorEdited: (contributor: ContributorResponseResource) => void;
	onContributorRemoved: (contributor: ContributorResponseResource) => void;
};

const roleService = container.resolve(RoleService);

export default function EditRoleModal(props: IProps) {
	const { projectId } = useProjectId();
	const { projectTeamId } = useProjectTeamContext();
	const { errors, handleError, clearErrors } = useForm();
	const { selectedContributor, isModalOpen, onCloseModal, onOpenCreateContributorModal, onOpenEditContributorModal, onOpenDeleteContributorModal } = useContributorModal();

	const onEditRole = useCallback(
		(formData: { [key: string]: unknown }) => {
			clearErrors();

			const roleRequestResource = ProjectTeamRoleContributorsRequestResource.hydrate<ProjectTeamRoleContributorsRequestResource>({
				projectTeamId,
				roleTitle: [{ lang: I18n.getLang(), text: formData["roleTitle"] as string }],
				index: parseInt(formData["index"] as string),
				contributors: props.role.contributors.map((contributor) => contributor.id),
			});

			roleRequestResource
				.validateOrReject()
				.then(() => roleService.update(projectId, projectTeamId, props.role.id, roleRequestResource))
				.then(props.onRoleEdited)
				.catch(handleError);
		},
		[clearErrors, handleError, projectId, projectTeamId, props.onRoleEdited, props.role.contributors, props.role.id],
	);

	const onContributorAdded = useCallback(
		(contributor: ContributorResponseResource) => {
			props.onContributorAdded(contributor);
			onCloseModal();
		},
		[onCloseModal, props],
	);

	const onContributorCreated = useCallback(
		(contributor: ContributorResponseResource) => {
			roleService.addContributor(projectId, projectTeamId, props.role.id, contributor.id).then(() => onContributorAdded(contributor));
		},
		[onContributorAdded, projectId, projectTeamId, props.role.id],
	);

	const onContributorEdited = useCallback(
		(contributor: ContributorResponseResource) => {
			props.onContributorEdited(contributor);
			onCloseModal();
		},
		[onCloseModal, props],
	);

	const onContributorRemoved = useCallback(
		(contributor: ContributorResponseResource) => {
			props.onContributorRemoved(contributor);
			onCloseModal();
		},
		[onCloseModal, props],
	);

	return (
		<Modal isOpen onClose={props.onClose} className={classes["root"]}>
			<div className={classes["header"]}>
				<Typography type="p" size="large" weight="bold">
					{I18n.asset.pages.professional_space.tabs.team.edit_role_modal.title}
				</Typography>
				<XMarkIcon className={classes["icon"]} onClick={props.onClose} />
			</div>

			<Form onSubmit={onEditRole} errors={errors} className={classes["form-container"]}>
				<Input
					name="roleTitle"
					required
					label={I18n.asset.pages.professional_space.tabs.team.edit_role_modal.role_name}
					additionalInfo={I18n.asset.pages.professional_space.tabs.team.edit_role_modal.role_name_max_length}
					defaultValue={translateISO369(props.role.roleTitle)}
				/>
				<Input
					name="index"
					type="number"
					min="1"
					max="1000"
					label={I18n.asset.pages.professional_space.tabs.team.edit_role_modal.role_order}
					defaultValue={props.role.index.toFixed()}
				/>
				<div className={classes["members-container"]}>
					<Typography type="p" weight="medium">
						{I18n.asset.pages.professional_space.tabs.team.edit_role_modal.role_members}
					</Typography>
					<div className={classes["members"]}>
						{props.role.contributors.map((contributor) => (
							<RoleMember
								key={contributor.id}
								contributor={contributor}
								onEdit={() => onOpenEditContributorModal(contributor)}
								onDelete={() => onOpenDeleteContributorModal(contributor)}
							/>
						))}
					</div>
				</div>
				<div className={classes["buttons-container"]}>
					<Button variant={EButtonVariant.OUTLINED} color={EButtonColor.PRIMARY} onClick={onOpenCreateContributorModal}>
						{I18n.asset.pages.professional_space.tabs.team.edit_role_modal.add_member}
					</Button>
					<Button type="submit" variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY}>
						{I18n.asset.pages.professional_space.tabs.team.edit_role_modal.edit_role}
					</Button>
				</div>
			</Form>

			{isModalOpen(EContributorModal.CREATE_CONTRIBUTOR) && <CreateContributorModal isOpen onCreated={onContributorCreated} onClose={onCloseModal} />}
			{isModalOpen(EContributorModal.EDIT_CONTRIBUTOR) && selectedContributor && (
				<EditContributorModal isOpen contributor={selectedContributor} onEdited={onContributorEdited} onClose={onCloseModal} />
			)}
			{isModalOpen(EContributorModal.DELETE_CONTRIBUTOR) && selectedContributor && (
				<RemoveContributorModal isOpen contributor={selectedContributor} onRemoved={onContributorRemoved} onClose={onCloseModal} />
			)}
		</Modal>
	);
}
